/**
 * Plant Mapping Component
 * Interactive plant layout with drag & drop workshop positioning
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { 
  ZoomIn, 
  ZoomOut, 
  Home, 
  Settings, 
  Eye, 
  EyeOff,
  Grid,
  Save,
  RotateCcw,
  Search,
  Filter,
  Maximize,
  Minimize,
  RefreshCw
} from 'lucide-react';
import { usePlantMappingStore } from '../../stores/plantMappingStore';
import { useDashboardStore } from '../../stores/dashboardStore';
import { useAuthStore } from '../../stores/authStore';
import { plantLayoutApi, plantMappingUtils } from '../../services/plantMapping';
import { WorkshopService } from '../../services/workshop';
import { apiClient } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import PlantCanvas from './PlantCanvas';
import WorkshopBubble from './WorkshopBubble';
import PlantZone from './PlantZone';
import UnpositionedQueue from './UnpositionedQueue';
import WorkshopTooltip from './WorkshopTooltip';
import PlantMappingControls from './PlantMappingControls';
import PlantMappingStats from './PlantMappingStats';
import CreateZoneModal from './CreateZoneModal';
import WorkshopDetailsModal from './WorkshopDetailsModal';

interface PlantMappingProps {
  plantId?: number;
  layoutId?: number;
}

const PlantMapping: React.FC<PlantMappingProps> = ({ plantId, layoutId: propLayoutId }) => {
  const { layoutId: paramLayoutId } = useParams<{ layoutId: string }>();
  const finalLayoutId = propLayoutId || (paramLayoutId ? parseInt(paramLayoutId) : null);
  const fallbackPlantId = plantId || 1; // Legacy fallback when no ALV filter is selected
  const { alvId: globalAlvId, setAlvIdFilter, deadlineFrom, deadlineTo } = useDashboardStore();
  const { user } = useAuthStore();
  const canEditLayout = user?.is_admin === true;

  // Store state
  const {
    layout,
    positions,
    zones,
    unpositionedWorkshops,
    statistics,
    loading,
    error,
    selectedWorkshop,
    selectedZone,
    isDragging,
    draggedWorkshop,
    zoom,
    centerX,
    centerY,
    showZones,
    showUnpositioned,
    filterStatus,
    filterCategory,
    filterDepartment,
    fetchDashboard,
    fetchDashboardByPlant,
    setZoom,
    setCenter,
    setShowZones,
    setShowUnpositioned,
    setSelectedWorkshop,
    setSelectedZone,
    setFilters,
    clearError,
    clearMappingData,
    setLayoutBackgroundOpacity,
    getFilteredPositions,
    getFilteredUnpositionedWorkshops
  } = usePlantMappingStore();

  // Local state
  const [showCreateZoneModal, setShowCreateZoneModal] = useState(false);
  const [showWorkshopModal, setShowWorkshopModal] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isResolvingPlant, setIsResolvingPlant] = useState(false);
  const [isSavingBackgroundOpacity, setIsSavingBackgroundOpacity] = useState(false);
  const [backgroundFitMode, setBackgroundFitMode] = useState<'cover' | 'contain' | 'stretch'>('cover');
  const [layoutMessage, setLayoutMessage] = useState<string | null>(null);
  const [availablePlantOptions, setAvailablePlantOptions] = useState<Array<{ value: string; label: string }>>([
    { value: '', label: 'All Plants' },
  ]);
  const [tooltipData, setTooltipData] = useState<{
    visible: boolean;
    x: number;
    y: number;
    workshop: any;
  }>({ visible: false, x: 0, y: 0, workshop: null });

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const saveOpacityTimeoutRef = useRef<number | null>(null);

  // Keep local plant filter aligned with global ALV filter.
  useEffect(() => {
    setFilters({ alvId: globalAlvId || null });
  }, [globalAlvId, setFilters]);

  // Load plant options used by page-side plant selector.
  useEffect(() => {
    const loadPlantOptions = async () => {
      try {
        const plants = await WorkshopService.getAvailablePlants({
          deadline_from: deadlineFrom,
          deadline_to: deadlineTo,
        });
        setAvailablePlantOptions([{ value: '', label: 'All Plants' }, ...plants]);
      } catch (error) {
        console.error('Failed to load plant options for mapping page:', error);
      }
    };

    loadPlantOptions();
  }, [deadlineFrom, deadlineTo]);

  const resolvePlantIdByAlv = useCallback(async (alvId: string) => {
    const response = await apiClient.get('/plants/', { params: { alv_id: alvId, page_size: 1 } });
    const plants = response.data?.results || response.data || [];
    return plants.length > 0 ? plants[0] : null;
  }, []);

  // Load dashboard data with ALV -> Plant resolution for /plant-mapping route.
  useEffect(() => {
    let isActive = true;

    const loadMappingData = async () => {
      if (finalLayoutId) {
        setLayoutMessage(null);
        await fetchDashboard(finalLayoutId);
        return;
      }

      if (!globalAlvId) {
        setLayoutMessage(null);
        await fetchDashboardByPlant(fallbackPlantId);
        return;
      }

      setIsResolvingPlant(true);
      try {
        const resolvedPlant = await resolvePlantIdByAlv(globalAlvId);
        if (!isActive) return;

        if (!resolvedPlant) {
          clearMappingData();
          setLayoutMessage(`No plant mapped to ALV '${globalAlvId}'.`);
          return;
        }

        const result = await fetchDashboardByPlant(resolvedPlant.id);
        if (!isActive) return;

        if (result === 'no_layout') {
          setLayoutMessage(`Plant '${resolvedPlant.name}' has no layout yet.`);
        } else {
          setLayoutMessage(null);
        }
      } catch (error) {
        if (!isActive) return;
        clearMappingData();
        setLayoutMessage(`Failed to resolve plant mapping for ALV '${globalAlvId}'.`);
      } finally {
        if (isActive) {
          setIsResolvingPlant(false);
        }
      }
    };

    loadMappingData();
    return () => {
      isActive = false;
    };
  }, [
    finalLayoutId,
    globalAlvId,
    fallbackPlantId,
    fetchDashboard,
    fetchDashboardByPlant,
    clearMappingData,
    resolvePlantIdByAlv
  ]);

  useEffect(() => {
    return () => {
      if (saveOpacityTimeoutRef.current) {
        window.clearTimeout(saveOpacityTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (saveOpacityTimeoutRef.current) {
      window.clearTimeout(saveOpacityTimeoutRef.current);
      saveOpacityTimeoutRef.current = null;
    }
    setIsSavingBackgroundOpacity(false);
  }, [layout?.id]);

  // Refresh data function
  const handleRefresh = useCallback(async () => {
    if (finalLayoutId) {
      await fetchDashboard(finalLayoutId);
      return;
    }

    if (globalAlvId) {
      setIsResolvingPlant(true);
      try {
        const resolvedPlant = await resolvePlantIdByAlv(globalAlvId);
        if (!resolvedPlant) {
          clearMappingData();
          setLayoutMessage(`No plant mapped to ALV '${globalAlvId}'.`);
          return;
        }
        const result = await fetchDashboardByPlant(resolvedPlant.id);
        if (result === 'no_layout') {
          setLayoutMessage(`Plant '${resolvedPlant.name}' has no layout yet.`);
        } else {
          setLayoutMessage(null);
        }
      } finally {
        setIsResolvingPlant(false);
      }
    } else {
      const result = await fetchDashboardByPlant(fallbackPlantId);
      if (result === 'no_layout') {
        setLayoutMessage('Selected plant has no layout configured yet.');
      } else {
        setLayoutMessage(null);
      }
    }
  }, [
    finalLayoutId,
    globalAlvId,
    fallbackPlantId,
    fetchDashboard,
    fetchDashboardByPlant,
    clearMappingData,
    resolvePlantIdByAlv
  ]);

  const handleBackgroundOpacityChange = useCallback((percentValue: number) => {
    if (!layout || !canEditLayout) return;

    const clampedPercent = Math.max(0, Math.min(100, percentValue));
    const normalizedOpacity = clampedPercent / 100;
    const layoutId = layout.id;

    setLayoutBackgroundOpacity(normalizedOpacity);
    setIsSavingBackgroundOpacity(true);

    if (saveOpacityTimeoutRef.current) {
      window.clearTimeout(saveOpacityTimeoutRef.current);
    }

    saveOpacityTimeoutRef.current = window.setTimeout(async () => {
      try {
        await plantLayoutApi.update(layoutId, { background_opacity: normalizedOpacity });
      } catch (error) {
        console.error('Failed to save layout opacity:', error);
      } finally {
        setIsSavingBackgroundOpacity(false);
      }
    }, 350);
  }, [layout, canEditLayout, setLayoutBackgroundOpacity]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setZoom(zoom * 1.2);
  }, [zoom, setZoom]);

  const handleZoomOut = useCallback(() => {
    setZoom(zoom / 1.2);
  }, [zoom, setZoom]);

  const handleResetView = useCallback(() => {
    if (layout) {
      setZoom(layout.default_zoom);
      setCenter(layout.default_center_x, layout.default_center_y);
    }
  }, [layout, setZoom, setCenter]);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  // Workshop selection
  const handleWorkshopClick = useCallback((workshop: any) => {
    setSelectedWorkshop(workshop);
    setShowWorkshopModal(true);
  }, [setSelectedWorkshop]);

  // Zone selection
  const handleZoneClick = useCallback((zone: any) => {
    setSelectedZone(zone);
  }, [setSelectedZone]);

  // Tooltip handlers
  const handleWorkshopHover = useCallback((workshop: any, event: React.MouseEvent) => {
    if (!isDragging) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        setTooltipData({
          visible: true,
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
          workshop
        });
      }
    }
  }, [isDragging]);

  const handleWorkshopLeave = useCallback(() => {
    setTooltipData(prev => ({ ...prev, visible: false }));
  }, []);

  // Get filtered positions
  const filteredPositions = getFilteredPositions();
  const filteredUnpositioned = getFilteredUnpositionedWorkshops();

  if ((loading || isResolvingPlant) && !layout && !layoutMessage) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">Error loading plant mapping</div>
          <div className="text-gray-600 mb-4">{error}</div>
          <button
            onClick={clearError}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-screen flex flex-col bg-gray-50 ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Header */}
      {!isFullscreen && (
        <div className="bg-white shadow-sm border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                Plant Mapping - {layout ? layout.name : (globalAlvId || 'No Layout')}
              </h1>
              {!canEditLayout && (
                <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 border border-amber-200">
                  View only
                </span>
              )}
            </div>
            <p className="text-gray-600 mt-1">
              {canEditLayout
                ? 'Interactive workshop positioning system'
                : 'View plant layout and workshop positions'}
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Stats */}
            {statistics && (
              <div className="flex items-center gap-6 text-sm">
                <div className="text-center">
                  <div className="font-semibold text-blue-600">
                    {statistics.positioned_workshops}
                  </div>
                  <div className="text-gray-500">Positioned</div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-orange-600">
                    {statistics.unpositioned_workshops}
                  </div>
                  <div className="text-gray-500">Unpositioned</div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-green-600">
                    {zones.length}
                  </div>
                  <div className="text-gray-500">Zones</div>
                </div>
              </div>
            )}
            
            {/* View Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefresh}
                className="p-2 rounded bg-green-100 text-green-600 hover:bg-green-200"
                title="Refresh workshop data"
                disabled={loading}
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              </button>
              
              <button
                onClick={() => setShowZones(!showZones)}
                className={`p-2 rounded ${
                  showZones 
                    ? 'bg-blue-100 text-blue-600' 
                    : 'bg-gray-100 text-gray-600'
                } hover:bg-blue-200`}
                title="Toggle zones"
              >
                {showZones ? <Eye size={16} /> : <EyeOff size={16} />}
              </button>
              
              <button
                onClick={() => setShowUnpositioned(!showUnpositioned)}
                className={`p-2 rounded ${
                  showUnpositioned 
                    ? 'bg-orange-100 text-orange-600' 
                    : 'bg-gray-100 text-gray-600'
                } hover:bg-orange-200`}
                title="Toggle unpositioned queue"
              >
                <Grid size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Controls Sidebar */}
        {!isFullscreen && (
          <div className="w-80 bg-white shadow-sm border-r flex flex-col">
          <PlantMappingControls
            onCreateZone={() => setShowCreateZoneModal(true)}
            canCreateZone={canEditLayout && !!layout}
            canEditLayout={canEditLayout}
            selectedPlantAlv={globalAlvId || ''}
            plantOptions={availablePlantOptions}
            onPlantChange={(alvId) => setAlvIdFilter(alvId || undefined)}
            backgroundOpacityPercent={(layout?.background_opacity ?? 0.8) * 100}
            onBackgroundOpacityChange={handleBackgroundOpacityChange}
            isSavingBackgroundOpacity={isSavingBackgroundOpacity}
            backgroundFitMode={backgroundFitMode}
            onBackgroundFitModeChange={setBackgroundFitMode}
          />
          
          {statistics && (
            <PlantMappingStats statistics={statistics} />
          )}
        </div>
        )}

        {/* Plant Canvas Area */}
        <div className="flex-1 relative" ref={containerRef}>
          {/* Unpositioned Queue */}
          {layout && showUnpositioned && filteredUnpositioned.length > 0 && (
            <UnpositionedQueue
              workshops={filteredUnpositioned}
              canEditLayout={canEditLayout}
              onWorkshopHover={handleWorkshopHover}
              onWorkshopLeave={handleWorkshopLeave}
              onWorkshopClick={handleWorkshopClick}
            />
          )}

          {/* Main Plant Canvas */}
          {layout ? (
            <div
              className="absolute inset-0"
              style={{
                top: showUnpositioned && filteredUnpositioned.length > 0 ? '80px' : '0'
              }}
            >
              <PlantCanvas
                ref={canvasRef}
                layout={layout}
                zoom={zoom}
                centerX={centerX}
                centerY={centerY}
                backgroundFitMode={backgroundFitMode}
                onZoomChange={setZoom}
                onCenterChange={setCenter}
              >
                {/* Plant Zones */}
                {showZones && zones.map(zone => (
                  <PlantZone
                    key={zone.id}
                    zone={zone}
                    layout={layout}
                    isSelected={selectedZone?.id === zone.id}
                    canEditLayout={canEditLayout}
                    onClick={() => handleZoneClick(zone)}
                  />
                ))}

                {/* Workshop Bubbles */}
                {filteredPositions.map(position => (
                  <WorkshopBubble
                    key={position.id}
                    position={position}
                    layout={layout}
                    isSelected={selectedWorkshop?.id === position.id}
                    isDragging={draggedWorkshop?.id === position.id}
                    canEditLayout={canEditLayout}
                    onHover={(e) => handleWorkshopHover(position, e)}
                    onLeave={handleWorkshopLeave}
                    onClick={() => handleWorkshopClick(position)}
                  />
                ))}
              </PlantCanvas>
            </div>
          ) : (
            <div className="absolute inset-0 bg-white flex items-center justify-center">
              <div className="text-center px-6">
                <div className="text-lg font-semibold text-gray-900 mb-2">No Layout Available</div>
                <div className="text-gray-600">
                  {layoutMessage || 'Selected plant has no layout configured yet.'}
                </div>
              </div>
            </div>
          )}

          {/* Zoom Controls */}
          <div className="absolute bottom-4 right-4 flex flex-col gap-2">
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-white shadow-lg rounded border hover:bg-gray-50"
              title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
              {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
            </button>
            <button
              onClick={handleZoomIn}
              disabled={!layout}
              className="p-2 bg-white shadow-lg rounded border hover:bg-gray-50"
              title="Zoom in"
            >
              <ZoomIn size={20} />
            </button>
            <button
              onClick={handleZoomOut}
              disabled={!layout}
              className="p-2 bg-white shadow-lg rounded border hover:bg-gray-50"
              title="Zoom out"
            >
              <ZoomOut size={20} />
            </button>
            <button
              onClick={handleResetView}
              disabled={!layout}
              className="p-2 bg-white shadow-lg rounded border hover:bg-gray-50"
              title="Reset view"
            >
              <Home size={20} />
            </button>
          </div>

          {/* Loading Overlay */}
          {loading && (
            <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
              <div className="bg-white p-4 rounded-lg shadow-lg">
                <LoadingSpinner />
                <div className="mt-2 text-sm text-gray-600">
                  {canEditLayout ? 'Updating positions...' : 'Loading layout...'}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tooltip */}
      <WorkshopTooltip
        visible={tooltipData.visible}
        x={tooltipData.x}
        y={tooltipData.y}
        workshop={tooltipData.workshop}
      />

      {/* Modals */}
      {canEditLayout && showCreateZoneModal && layout && (
        <CreateZoneModal
          layout={layout}
          onClose={() => setShowCreateZoneModal(false)}
        />
      )}

      {showWorkshopModal && selectedWorkshop && (
        <WorkshopDetailsModal
          workshop={selectedWorkshop}
          canEditLayout={canEditLayout}
          onClose={() => {
            setShowWorkshopModal(false);
            setSelectedWorkshop(null);
          }}
        />
      )}
    </div>
  );
};

export default PlantMapping;
